import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import axios from 'axios';
import { format } from 'date-fns';
import MarketOverviewDark from './MarketOverviewDark';
import D3CandlestickChart from './D3CandlestickChart';
import './PreMarketDashboard.css';

// Use empty string to make relative URLs (works with any host/IP)
const API_URL = process.env.REACT_APP_API_URL || '';

const PreMarketDashboard = () => {
  console.log('🎯 PreMarketDashboard component loaded!');
  const [scans, setScans] = useState([]);
  const [selectedScan, setSelectedScan] = useState(null);
  const [currentData, setCurrentData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('small_cap_gappers');
  const [selectedStock, setSelectedStock] = useState(null);
  const [selectedNews, setSelectedNews] = useState(null);
  const [loadingNews, setLoadingNews] = useState(false);
  const [selectedChart, setSelectedChart] = useState(null);
  const [panelOrder, setPanelOrder] = useState([]); // Track order of panels: ['news', 'chart']
  const [chartModal, setChartModal] = useState(null); // For full-screen chart popup
  const [autoRefresh, setAutoRefresh] = useState(false);
  const [refreshInterval] = useState(60);
  const [scanRunning, setScanRunning] = useState(false);
  const [scanProgress, setScanProgress] = useState('');
  const [topMoversModal, setTopMoversModal] = useState(null); // For top movers analysis popup
  const [analyzingTopMovers, setAnalyzingTopMovers] = useState(false);
  const [filters, setFilters] = useState({
    minPrice: 0,
    maxPrice: 10000,
    minGap: -100,
    maxGap: 1000,
    minVolume: 0,
    minRelVolume: 0,
    newsFilter: 'all',
    sortBy: 'gap_pct',
    sortDesc: true
  });

  // Load cache from localStorage on mount
  const [openPriceCache, setOpenPriceCache] = useState(() => {
    try {
      const cached = localStorage.getItem('openPriceCache');
      return cached ? JSON.parse(cached) : {};
    } catch (e) {
      console.error('Failed to load cache from localStorage:', e);
      return {};
    }
  });

  // Debug state changes
  useEffect(() => {
    console.log('📝 selectedScan state changed to:', selectedScan);
  }, [selectedScan]);

  useEffect(() => {
    console.log('📝 currentData state changed, has top_movers:', !!currentData?.top_movers);
  }, [currentData]);

  // Save cache to localStorage whenever it changes
  useEffect(() => {
    try {
      localStorage.setItem('openPriceCache', JSON.stringify(openPriceCache));
    } catch (e) {
      console.error('Failed to save cache to localStorage:', e);
    }
  }, [openPriceCache]);

  const watchlists = {
    small_cap_gappers: { name: 'Small Cap Gappers', icon: '📈', key: 'small_cap_gappers' },
    large_cap_gappers: { name: 'Large Cap Gappers', icon: '💼', key: 'large_cap_gappers' },
    gap_downs: { name: 'Gap Downs', icon: '📉', key: 'gap_downs' },
    low_float_runners: { name: 'Low Float', icon: '🚀', key: 'low_float_runners' },
    penny_stock_movers: { name: 'Penny Stocks', icon: '🪙', key: 'penny_stock_movers' }
  };

  // Column resizing refs and state
  const tableRef = useRef(null);
  const [columnWidths, setColumnWidths] = useState({});
  const [isResizing, setIsResizing] = useState(false);
  const [currentColumn, setCurrentColumn] = useState(null);
  const [startX, setStartX] = useState(0);
  const [startWidth, setStartWidth] = useState(0);

  // Handle column resize
  const handleMouseDown = useCallback((e, columnName) => {
    e.preventDefault();
    e.stopPropagation();
    setIsResizing(true);
    setCurrentColumn(columnName);
    setStartX(e.clientX);
    const th = e.target.closest('th');
    setStartWidth(th.offsetWidth);
  }, []);

  const handleMouseMove = useCallback((e) => {
    if (!isResizing || !currentColumn) return;
    const diff = e.clientX - startX;
    const newWidth = Math.max(50, startWidth + diff); // Minimum width of 50px
    setColumnWidths(prev => ({
      ...prev,
      [currentColumn]: newWidth
    }));
  }, [isResizing, currentColumn, startX, startWidth]);

  const handleMouseUp = useCallback(() => {
    setIsResizing(false);
    setCurrentColumn(null);
  }, []);

  // Add global mouse event listeners for resizing
  useEffect(() => {
    if (isResizing) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = 'col-resize';
      document.body.style.userSelect = 'none';

      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
        document.body.style.cursor = '';
        document.body.style.userSelect = '';
      };
    }
  }, [isResizing, handleMouseMove, handleMouseUp]);

  // Run premarket scan with streaming progress
  const [scanPercent, setScanPercent] = useState(0);

  const runPremarketScan = async () => {
    setScanRunning(true);
    setScanProgress('Initializing scanner...');
    setScanPercent(0);

    try {
      // Use Server-Sent Events for real-time progress
      // EventSource doesn't work through webpack proxy, so use direct URL
      const streamUrl = API_URL || 'http://localhost:3002';
      const eventSource = new EventSource(`${streamUrl}/api/run-premarket-scan-stream`);

      eventSource.onmessage = async (event) => {
        const data = JSON.parse(event.data);

        if (data.type === 'progress') {
          setScanPercent(data.percent || 0);
          if (data.message) setScanProgress(data.message);
        } else if (data.type === 'status') {
          setScanProgress(data.message);
        } else if (data.type === 'complete') {
          setScanProgress('✅ ' + data.message);
          setScanPercent(100);
          eventSource.close();

          // Refresh the scans list and load new scan
          await fetchScans();
          if (data.scanId) {
            await loadScan(data.scanId);
          }

          setTimeout(() => {
            setScanRunning(false);
            setScanProgress('');
            setScanPercent(0);
          }, 2000);
        } else if (data.type === 'error') {
          setScanProgress('❌ ' + data.message);
          eventSource.close();
          setTimeout(() => {
            setScanRunning(false);
            setScanProgress('');
            setScanPercent(0);
          }, 3000);
        }
      };

      eventSource.onerror = (error) => {
        console.error('SSE error:', error);
        eventSource.close();
        setScanProgress('❌ Connection lost');
        setTimeout(() => {
          setScanRunning(false);
          setScanProgress('');
          setScanPercent(0);
        }, 3000);
      };

    } catch (error) {
      console.error('Error running premarket scan:', error);
      setScanProgress('❌ Scan failed');
      setTimeout(() => {
        setScanRunning(false);
        setScanProgress('');
        setScanPercent(0);
      }, 3000);
    }
  };

  // Analyze top movers for current scan
  const analyzeTopMovers = async () => {
    if (!selectedScan) {
      alert('Please select a scan first');
      return;
    }

    setAnalyzingTopMovers(true);

    try {
      const response = await axios.post(`${API_URL}/api/analyze-top-movers`, {
        scanId: selectedScan
      });

      if (response.data.success) {
        setTopMoversModal({
          topMovers: response.data.topMovers,
          totalFiltered: response.data.totalFiltered
        });
      } else {
        throw new Error(response.data.message || 'Analysis failed');
      }
    } catch (error) {
      console.error('Error analyzing top movers:', error);
      alert('Failed to analyze top movers: ' + error.message);
    } finally {
      setAnalyzingTopMovers(false);
    }
  };

  // Fetch available scans
  const fetchScans = async () => {
    console.log('🔍 fetchScans() called, API_URL:', API_URL);
    try {
      console.log('📡 Making API request to:', `${API_URL}/api/scans`);
      const response = await axios.get(`${API_URL}/api/scans`);
      console.log('✅ fetchScans success:', response.data);
      console.log('🗂️ Number of scans found:', response.data.length);
      console.log('🗂️ First few scans:', response.data.slice(0, 3));

      // Filter to keep only the best scan per day (prefer scans with news data, then most recent)
      const scansByDate = {};
      response.data.forEach(scan => {
        const timestamp = scan.timestamp || scan.date;
        if (timestamp) {
          const date = new Date(timestamp).toISOString().split('T')[0]; // Get YYYY-MM-DD

          if (!scansByDate[date]) {
            scansByDate[date] = scan;
          } else {
            // Prefer scans that have news checked
            const currentHasNews = scansByDate[date].id?.includes('with_news') || scansByDate[date].filename?.includes('with_news');
            const newHasNews = scan.id?.includes('with_news') || scan.filename?.includes('with_news');

            if (newHasNews && !currentHasNews) {
              scansByDate[date] = scan;
            } else if (newHasNews === currentHasNews) {
              // If both have or don't have news, keep the more recent one
              if (new Date(timestamp) > new Date(scansByDate[date].timestamp || scansByDate[date].date)) {
                scansByDate[date] = scan;
              }
            }
          }
        }
      });

      // Convert back to array and sort by date descending
      const uniqueScans = Object.values(scansByDate).sort((a, b) => {
        const dateA = new Date(a.timestamp || a.date);
        const dateB = new Date(b.timestamp || b.date);
        return dateB - dateA; // Most recent first
      });

      console.log('🗂️ Filtered to unique scans per day:', uniqueScans.length);
      setScans(uniqueScans);

      // Auto-select latest scan if none selected
      if (!selectedScan && uniqueScans.length > 0) {
        console.log('🎯 Auto-selecting scan:', uniqueScans[0].id);
        await loadScan(uniqueScans[0].id);
      } else {
        console.log('🔄 selectedScan already exists or no scans available:', selectedScan, response.data.length);
      }
    } catch (error) {
      console.error('❌ Error fetching scans:', error);
      console.error('Error details:', error.response?.data, error.message);
    }
  };

  // Load specific scan data
  const loadScan = async (scanId) => {
    console.log('📂 loadScan() called with scanId:', scanId);
    setLoading(true);
    try {
      console.log('📡 Making API request to:', `${API_URL}/api/scans/${scanId}`);
      const response = await axios.get(`${API_URL}/api/scans/${scanId}`);
      console.log('✅ loadScan success for', scanId, '- data keys:', Object.keys(response.data));
      console.log('📊 Scan timestamp:', response.data.timestamp);
      console.log('📊 Total top_movers tabs:', Object.keys(response.data.top_movers || {}));
      setCurrentData(response.data);
      setSelectedScan(scanId);
      console.log('🔄 About to set selectedScan to:', scanId);

      // Directly call fetchOpenPrices after successful scan loading
      console.log('🚀 Directly calling fetchOpenPrices after scan load');
      fetchOpenPricesForScan(response.data, scanId);

      // Also fetch current prices to update LAST column
      console.log('🔴 Calling fetchCurrentPrices after scan load');
      fetchCurrentPrices(response.data);
    } catch (error) {
      console.error('❌ Error loading scan:', scanId, error);
      console.error('Error details:', error.response?.data, error.message);
    } finally {
      setLoading(false);
    }
  };

  // Fetch news for a stock
  const fetchNews = async (symbol) => {
    if (!symbol) return;

    setLoadingNews(true);
    setSelectedNews({ symbol, loading: true });

    try {
      const response = await axios.get(`${API_URL}/api/news/${symbol}`);
      setSelectedNews({
        symbol,
        articles: response.data.news || [],
        loading: false
      });
    } catch (error) {
      console.error('Error fetching news:', error);
      setSelectedNews({
        symbol,
        articles: [],
        loading: false,
        error: 'Failed to fetch news'
      });
    } finally {
      setLoadingNews(false);
    }
  };

  // Enrich stock data with additional fields
  const enrichStockData = (stock) => {
    // Add simulated data if not present
    const enriched = { ...stock };

    // Check if we have a cached open price for this stock and current scan date
    const scanDate = currentData?.timestamp ?
      new Date(currentData.timestamp).toISOString().split('T')[0] :
      selectedScan?.timestamp ? new Date(selectedScan.timestamp).toISOString().split('T')[0] :
      new Date().toISOString().split('T')[0];
    const cacheKey = `${stock.symbol}_${scanDate}`;
    const cachedOpenPrice = openPriceCache[cacheKey];

    // Debug the first stock to show scan date calculation
    if (stock.symbol === 'MSS' || stock.symbol === 'DWTX' || stock.symbol === 'TLRY') {
      console.log(`🗓️ ${stock.symbol}: scanDate=${scanDate}, currentData.timestamp=${currentData?.timestamp}, selectedScan.timestamp=${selectedScan?.timestamp}`);
    }

    // Use cached open price if stock doesn't have one
    if (cachedOpenPrice && (!enriched.open_price || enriched.open_price === null)) {
      console.log(`🔧 ${stock.symbol}: Using cached open price ${cachedOpenPrice.open_price} from ${scanDate}`);
      enriched.open_price = cachedOpenPrice.open_price;
    } else if (!enriched.open_price || enriched.open_price === null) {
      console.log(`⚠️ ${stock.symbol}: No open price in stock data or cache for ${scanDate}`);
    }

    // Get current time and scan time
    const now = new Date();
    const scanTime = currentData?.timestamp ? new Date(currentData.timestamp) : now;

    // Get dates as YYYY-MM-DD strings for reliable comparison
    const todayStr = now.toISOString().split('T')[0];
    const scanDateStr = scanTime.toISOString().split('T')[0];
    const isToday = todayStr === scanDateStr;

    // Get current time in Eastern Time (account for DST)
    const nowET = new Date(now.toLocaleString('en-US', { timeZone: 'America/New_York' }));
    const currentHour = nowET.getHours();
    const currentMinute = nowET.getMinutes();

    console.log(`🕐 Date check: today=${todayStr}, scan=${scanDateStr}, isToday=${isToday}, currentET=${nowET.toLocaleString()}, hour=${currentHour}:${currentMinute}`);

    // Market hours: 4am-8pm EST (pre-market 4am-9:30am, regular 9:30am-4pm, after-hours 4pm-8pm)
    const isMarketHours = currentHour >= 4 && currentHour < 20;
    const isPreMarket = isMarketHours && (currentHour < 9 || (currentHour === 9 && currentMinute < 30));
    const isRegularMarket = isMarketHours && ((currentHour === 9 && currentMinute >= 30) || (currentHour > 9 && currentHour < 16));

    if (isToday && isMarketHours) {
      // Case 1: Viewing current day's scan during market hours

      if (isPreMarket) {
        // Before 9:30am: Gap % = current price - previous close
        // % From Open = N/A (market hasn't opened yet)
        if (enriched.prev_close && enriched.prev_close > 0) {
          enriched.gap_pct = ((enriched.price - enriched.prev_close) / enriched.prev_close) * 100;
        }
        enriched.change_from_open = null; // No open price yet
      } else {
        // After 9:30am: Need open price
        // Only calculate if we have actual open price data
        if (enriched.open_price && enriched.open_price > 0) {
          // Gap % = open price - previous close
          if (enriched.prev_close && enriched.prev_close > 0) {
            enriched.gap_pct = ((enriched.open_price - enriched.prev_close) / enriched.prev_close) * 100;
          }
          // % From Open = current price - open price
          enriched.change_from_open = ((enriched.price - enriched.open_price) / enriched.open_price) * 100;
          console.log(`📊 ${enriched.symbol} (live): open=${enriched.open_price}, current=${enriched.price}, change_from_open=${enriched.change_from_open.toFixed(2)}%`);
        } else {
          // If no open price available, set to null
          enriched.change_from_open = null;
          console.log(`❌ ${enriched.symbol} (live): No open price available (open_price=${enriched.open_price})`);
          // Calculate gap based on current price vs previous close
          if (enriched.prev_close && enriched.prev_close > 0) {
            enriched.gap_pct = ((enriched.price - enriched.prev_close) / enriched.prev_close) * 100;
          }
        }
      }
    } else {
      // Case 2: After market hours OR viewing previous day's scan
      // Gap % = that day's open - previous close
      // % From Open = that day's close - that day's open

      // Only calculate if we have actual open price data
      if (enriched.open_price && enriched.open_price > 0) {
        // Gap % = open - previous close
        if (enriched.prev_close && enriched.prev_close > 0) {
          enriched.gap_pct = ((enriched.open_price - enriched.prev_close) / enriched.prev_close) * 100;
        }
        // % From Open = close - open (for historical scans, use close_price not current price)
        const priceToUse = enriched.close_price || enriched.price;
        enriched.change_from_open = ((priceToUse - enriched.open_price) / enriched.open_price) * 100;
        console.log(`📊 ${enriched.symbol}: open=${enriched.open_price}, close=${enriched.close_price}, price=${enriched.price}, using=${priceToUse}, change_from_open=${enriched.change_from_open.toFixed(2)}%`);
      } else {
        // If no open price available, set to null
        enriched.change_from_open = null;
        console.log(`❌ ${enriched.symbol}: No open price available (open_price=${enriched.open_price})`);
        // Calculate gap based on current price vs previous close
        if (enriched.prev_close && enriched.prev_close > 0) {
          enriched.gap_pct = ((enriched.price - enriched.prev_close) / enriched.prev_close) * 100;
        }
      }
    }

    if (!enriched.avg_volume) {
      // Simulate average volume based on current volume
      enriched.avg_volume = enriched.volume ? enriched.volume / (1.5 + Math.random() * 2) : 0;
    }

    if (!enriched.atr) {
      // Simulate ATR as 2-5% of price
      enriched.atr = enriched.price * (0.02 + Math.random() * 0.03);
    }

    if (!enriched.rsi) {
      // Simulate RSI based on gap percentage
      if (enriched.gap_pct > 10) {
        enriched.rsi = 65 + Math.random() * 20; // Overbought tendency
      } else if (enriched.gap_pct < -5) {
        enriched.rsi = 20 + Math.random() * 20; // Oversold tendency
      } else {
        enriched.rsi = 40 + Math.random() * 30; // Neutral
      }
    }

    if (!enriched.bid || !enriched.ask) {
      // Simulate bid/ask spread (0.1-0.5% of price)
      const spread = enriched.price * (0.001 + Math.random() * 0.004);
      enriched.bid = enriched.price - spread / 2;
      enriched.ask = enriched.price + spread / 2;
    }

    if (!enriched.market_cap) {
      // Estimate market cap based on price and typical shares outstanding
      const sharesOutstanding = (10 + Math.random() * 100) * 1000000; // 10M to 110M shares
      enriched.market_cap = enriched.price * sharesOutstanding;
    }

    if (!enriched.float_shares) {
      // Float is typically 70-95% of shares outstanding
      const sharesOutstanding = enriched.market_cap / enriched.price;
      enriched.float_shares = sharesOutstanding * (0.7 + Math.random() * 0.25);
    }

    return enriched;
  };

  // Get current watchlist data
  const currentWatchlist = useMemo(() => {
    if (!currentData?.top_movers?.[activeTab]) return [];

    let stocks = [...currentData.top_movers[activeTab]].map(enrichStockData);

    // Apply filters
    stocks = stocks.filter(stock => {
      // Price filter
      if (filters.minPrice !== '' && stock.price < filters.minPrice) return false;
      if (filters.maxPrice !== '' && stock.price > filters.maxPrice) return false;

      // Gap filter
      if (filters.minGap !== '' && stock.gap_pct < filters.minGap) return false;
      if (filters.maxGap !== '' && stock.gap_pct > filters.maxGap) return false;

      // Volume filter
      if (filters.minVolume && stock.volume < filters.minVolume) return false;

      // Relative volume filter
      if (filters.minRelVolume && stock.avg_volume > 0) {
        const relVol = stock.volume / stock.avg_volume;
        if (relVol < filters.minRelVolume) return false;
      }

      // Market cap filter
      if (filters.minMarketCap && stock.market_cap < filters.minMarketCap) return false;
      if (filters.maxMarketCap && stock.market_cap > filters.maxMarketCap) return false;

      // Float filter
      if (filters.minFloat && stock.float_shares < filters.minFloat) return false;
      if (filters.maxFloat && stock.float_shares > filters.maxFloat) return false;

      // RSI filter
      if (filters.minRSI && stock.rsi < filters.minRSI) return false;
      if (filters.maxRSI && stock.rsi > filters.maxRSI) return false;

      // News filter
      if (filters.newsFilter === 'with_news' && !stock.has_news) return false;
      if (filters.newsFilter === 'no_news' && stock.has_news) return false;

      return true;
    });

    // Apply sorting
    stocks.sort((a, b) => {
      let aVal = a[filters.sortBy];
      let bVal = b[filters.sortBy];

      // Handle null values - treat null as lowest value
      if (aVal === null || aVal === undefined) aVal = filters.sortDesc ? -Infinity : Infinity;
      if (bVal === null || bVal === undefined) bVal = filters.sortDesc ? -Infinity : Infinity;

      // Convert to 0 if still not a number
      if (typeof aVal !== 'number') aVal = 0;
      if (typeof bVal !== 'number') bVal = 0;

      return filters.sortDesc ? bVal - aVal : aVal - bVal;
    });

    return stocks;
  }, [currentData, activeTab, filters, openPriceCache]);

  // Auto-refresh
  useEffect(() => {
    let interval;
    if (autoRefresh) {
      interval = setInterval(() => {
        if (selectedScan) {
          loadScan(selectedScan);
        }
      }, refreshInterval * 1000);
    }
    return () => clearInterval(interval);
  }, [autoRefresh, refreshInterval, selectedScan]);


  // Initial load - load scans and select the 9/29 morning scan if available
  useEffect(() => {
    console.log('🚀 useEffect triggered - loading default scan');
    const loadDefaultScan = async () => {
      try {
        console.log('📋 Starting to fetch all available scans');
        // First fetch all available scans
        await fetchScans();

        // Try to load the 9/26 scan for testing historical open prices
        const morningScans = ['scan-2025-09-26T09-33-00-000Z', 'scan-2025-09-29T08-55-00-000Z', 'scan-2025-09-29T01-55-56-968Z'];

        for (const scanId of morningScans) {
          try {
            const response = await axios.get(`${API_URL}/api/scans/${scanId}`);
            const data = response.data;

            // Create scan object
            const scan = {
              id: scanId,
              filename: `${scanId}.json`,
              timestamp: data.timestamp,
              totalScanned: data.total_scanned || 0,
              summary: data.summary || {}
            };

            setSelectedScan(scan);
            setCurrentData(data);
            console.log('Loaded scan data:', data);
            break; // Stop after loading the first available scan
          } catch (error) {
            console.log(`Scan ${scanId} not available, trying next...`);
          }
        }
      } catch (error) {
        console.error('Error loading default scan:', error);
      }
    };

    loadDefaultScan();
  }, []);

  // Function to fetch current live prices for the LAST column
  const fetchCurrentPrices = async (scanData) => {
    console.log('🔴 fetchCurrentPrices() called with scanData:', !!scanData);
    console.log('🔴 activeTab:', activeTab);

    if (!scanData || !scanData.top_movers) {
      console.log('🔴 Missing scan data or top_movers');
      return;
    }

    // Get all stocks from current active tab
    const currentStocks = scanData.top_movers[activeTab] || [];
    console.log('🔴 currentStocks:', currentStocks.length);
    const symbols = currentStocks.map(stock => stock.symbol);
    console.log('🔴 symbols to fetch:', symbols);

    if (symbols.length === 0) {
      console.log('🔴 No symbols to fetch current prices for');
      return;
    }

    console.log('🔴 Fetching current prices for', symbols.length, 'symbols');

    try {
      const response = await axios.post(`${API_URL}/api/current-prices`, {
        symbols: symbols
      });

      const currentPrices = response.data;
      console.log('🔴 Got current prices response:', Object.keys(currentPrices).length, 'symbols');
      console.log('🔴 Sample price data:', currentPrices[symbols[0]]);

      // Update only the LAST price (stock.price) in current data
      setCurrentData(prevData => {
        console.log('🔴 prevData:', !!prevData, 'activeTab in prevData:', activeTab);
        const updatedData = { ...prevData };
        if (updatedData.top_movers[activeTab]) {
          console.log('🔴 Updating stocks in tab:', activeTab);
          updatedData.top_movers[activeTab] = updatedData.top_movers[activeTab].map(stock => {
            const priceData = currentPrices[stock.symbol];
            if (priceData && priceData.last_price) {
              console.log(`🔴 Updated LAST for ${stock.symbol}: old=${stock.price} new=${priceData.last_price}`);
              return {
                ...stock,
                price: priceData.last_price  // Update only LAST column
              };
            } else {
              console.log(`🔴 No price data for ${stock.symbol}`);
            }
            return stock;
          });
        } else {
          console.log('🔴 activeTab not found in updatedData.top_movers');
        }
        return updatedData;
      });

      console.log('🔴 Current prices updated successfully');

    } catch (error) {
      console.error('🔴 Error fetching current prices:', error);
      console.error('🔴 Error details:', error.response?.data || error.message);
    }
  };

  // Function to fetch open prices for a specific scan
  const fetchOpenPricesForScan = async (scanData, scanId) => {
    console.log('💰 fetchOpenPricesForScan() called - scanData:', !!scanData, 'scanId:', scanId);
    if (!scanData?.top_movers) {
      console.log('💰 Skipping fetchOpenPricesForScan - no top_movers data');
      return;
    }

    // Get stocks from the current active tab only (fetch all stocks, no limit)
    const currentTabStocks = scanData.top_movers[activeTab] || [];
    const allStocks = Array.isArray(currentTabStocks) ? currentTabStocks : [];

    if (allStocks.length === 0) {
      console.log('💰 No stocks found in current tab:', activeTab);
      return;
    }

    // Determine the date of the scan
    const scanDate = scanData.timestamp ?
      new Date(scanData.timestamp).toISOString().split('T')[0] :
      new Date().toISOString().split('T')[0];

    // Check if this is today's scan or historical
    const today = new Date().toISOString().split('T')[0];
    const isToday = scanDate === today;

    console.log('💰 Fetch open prices - scanDate:', scanDate, 'today:', today, 'isToday:', isToday);

    // For today's scan, only fetch if market has opened (after 9:30 AM ET)
    if (isToday) {
      const now = new Date();
      const hours = now.getHours();
      const minutes = now.getMinutes();
      const marketHasOpened = (hours === 9 && minutes >= 30) || (hours > 9);

      console.log(`💰 Time check - hours: ${hours}, minutes: ${minutes}, marketHasOpened: ${marketHasOpened}`);

      if (!marketHasOpened) {
        console.log('💰 Market has not opened yet for today - skipping');
        return; // Market hasn't opened yet for today
      }

      console.log('💰 Market has opened today - proceeding to fetch open prices');
    }

    // Check cache first - get symbols that need open/close prices for this date
    const symbolsNeedingOpenPrice = allStocks.filter(stock => {
      const cacheKey = `${stock.symbol}_${scanDate}`;
      const hasCachedPrice = openPriceCache[cacheKey];
      const hasStockPrice = stock.open_price && stock.open_price !== null;
      const hasClosePrice = stock.close_price && stock.close_price !== null;
      // Need to fetch if missing cache OR missing open_price OR missing close_price
      return !hasCachedPrice || !hasStockPrice || !hasClosePrice;
    }).map(stock => stock.symbol);

    console.log('💰 Symbols needing open prices:', symbolsNeedingOpenPrice.length, symbolsNeedingOpenPrice.slice(0, 5));

    if (symbolsNeedingOpenPrice.length === 0) {
      console.log('💰 All stocks already have open/close prices - applying cached prices');
      // Apply cached prices to stocks that don't have them (only current tab)
      const updatedData = { ...scanData };
      if (updatedData.top_movers[activeTab]) {
        updatedData.top_movers[activeTab] = updatedData.top_movers[activeTab].map(stock => {
          const cacheKey = `${stock.symbol}_${scanDate}`;
          const cachedPrice = openPriceCache[cacheKey];
          if (cachedPrice) {
            return {
              ...stock,
              open_price: cachedPrice.open_price || stock.open_price,
              close_price: cachedPrice.close_price || stock.close_price,
              high_price: cachedPrice.high_price || stock.high_price,
              low_price: cachedPrice.low_price || stock.low_price
            };
          }
          return stock;
        });
      }
      setCurrentData(updatedData);
      return; // All stocks already have open/close prices
    }

    try {
      // Always use historical endpoint with date to get open prices from market open
      const endpoint = `/api/open-prices/${scanDate}`;
      console.log('💰 Making API call:', `${API_URL}${endpoint}`, 'symbols:', symbolsNeedingOpenPrice.slice(0, 5));

      const response = await axios.post(`${API_URL}${endpoint}`, {
        symbols: symbolsNeedingOpenPrice
      });

      const openPrices = response.data;
      console.log('💰 Got open prices response:', Object.keys(openPrices).length, 'symbols');
      console.log('💰 First symbol data:', openPrices[Object.keys(openPrices)[0]]);

      // Update cache and current data
      const newCacheEntries = {};
      const updatedData = { ...scanData };

      // Only update the current active tab
      if (updatedData.top_movers[activeTab]) {
        updatedData.top_movers[activeTab] = updatedData.top_movers[activeTab].map(stock => {
          const priceData = openPrices[stock.symbol];
          console.log(`💰 Processing ${stock.symbol} - priceData:`, priceData);
          if (priceData && priceData.open_price) {
            const cacheKey = `${stock.symbol}_${scanDate}`;
            newCacheEntries[cacheKey] = {
              open_price: priceData.open_price,
              close_price: priceData.close_price,
              high_price: priceData.high_price,
              low_price: priceData.low_price,
              date: scanDate
            };

            console.log(`💰 Updated ${stock.symbol} - open: ${priceData.open_price}, close: ${priceData.close_price}, keeping price: ${stock.price}`);

            const updatedStock = {
              ...stock,
              premarket_price: stock.price,  // Save original premarket scan price
              open_price: priceData.open_price,
              close_price: priceData.close_price,
              // Keep stock.price as-is - it will be updated by fetchCurrentPrices
              high_price: priceData.high_price,
              low_price: priceData.low_price
            };

            console.log(`💰 After update ${stock.symbol} - price field: ${updatedStock.price}`);
            return updatedStock;
          }
          return stock;
        });
      }

      // Update cache
      setOpenPriceCache(prev => ({ ...prev, ...newCacheEntries }));
      // Update current data
      setCurrentData(updatedData);
      console.log('💰 Successfully updated', Object.keys(newCacheEntries).length, 'open prices');

      // Save prices to scan file for persistence
      try {
        // Convert cache format (symbol_date) to symbol-only format for backend
        const priceUpdatesBySymbol = {};
        Object.entries(newCacheEntries).forEach(([cacheKey, prices]) => {
          const symbol = cacheKey.split('_')[0];
          priceUpdatesBySymbol[symbol] = prices;
        });

        console.log('💾 Attempting to save prices - scanFile:', scanId);
        console.log('💾 Price updates:', priceUpdatesBySymbol);

        const saveResponse = await axios.post(`${API_URL}/api/save-scan-prices`, {
          scanFile: scanId,
          priceUpdates: priceUpdatesBySymbol
        });
        console.log('💾 Save response:', saveResponse.data);
      } catch (saveError) {
        console.error('💾 Failed to save prices to file:', saveError);
        console.error('💾 Error details:', saveError.response?.data);
      }
    } catch (error) {
      console.error('💰 Failed to fetch open prices:', error);
      // Add specific error handling for rate limits
      if (error.response?.status === 429) {
        console.log('💰 Rate limited - will retry on tab change');
      }
    }
  };

  // Auto-refresh current prices every 5 minutes for today's scans
  useEffect(() => {
    console.log('🔴 useEffect triggered - selectedScan:', selectedScan, 'activeTab:', activeTab);
    console.log('🔴 currentData:', !!currentData, 'timestamp:', currentData?.timestamp);

    if (!currentData || !currentData.timestamp) {
      console.log('🔴 No currentData or timestamp - exiting');
      return;
    }

    // Check if this is today's scan (use local date, not UTC)
    const scanDate = currentData.timestamp.split('T')[0];
    const now = new Date();
    const today = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
    const isToday = scanDate === today;

    console.log('🔴 scanDate:', scanDate, 'today:', today, 'isToday:', isToday);

    if (!isToday) {
      console.log('🔴 Not today\'s scan - skipping auto-refresh');
      return;
    }

    console.log('🔴 Setting up 5-minute auto-refresh for today\'s scan');

    // Fetch current prices immediately on load
    console.log('🔴 Calling fetchCurrentPrices immediately');
    fetchCurrentPrices(currentData);

    // Set up 5-minute interval (300000ms)
    const intervalId = setInterval(() => {
      console.log('🔴 Auto-refresh triggered - fetching current prices');
      // Use the latest currentData from closure
      setCurrentData(prevData => {
        if (prevData && prevData.top_movers && prevData.top_movers[activeTab]) {
          fetchCurrentPrices(prevData);
        }
        return prevData;
      });
    }, 300000);

    console.log('🔴 Interval set up with ID:', intervalId);

    // Cleanup interval on unmount or when dependencies change
    return () => {
      console.log('🔴 Cleaning up auto-refresh interval');
      clearInterval(intervalId);
    };
  }, [selectedScan, activeTab]);  // Only re-run when scan or tab changes, NOT when currentData changes

  // Fetch open prices when tab changes (only if stocks in that tab don't have open_price yet)
  useEffect(() => {
    console.log('🔄 Tab changed to:', activeTab);
    if (currentData && selectedScan) {
      console.log('🔄 Calling fetchOpenPricesForScan for tab:', activeTab);
      fetchOpenPricesForScan(currentData, selectedScan);
    }
  }, [activeTab]);  // Re-run when activeTab changes

  // Helper function to determine if market is closed
  const isMarketClosed = () => {
    const now = new Date();
    const hours = now.getHours();
    const minutes = now.getMinutes();

    // Market is closed before 9:30 AM or after 4:00 PM ET
    if (hours < 9 || (hours === 9 && minutes < 30)) {
      return true; // Before market open
    }
    if (hours >= 16) {
      return true; // After market close
    }
    return false; // Market is currently open
  };

  // Helper function to check if scan is from today
  const isTodayScan = (scanData) => {
    if (!scanData || !scanData.timestamp) return false;
    const scanDate = scanData.timestamp.split('T')[0];
    const today = new Date().toISOString().split('T')[0];
    return scanDate === today;
  };


  const formatVolume = (volume) => {
    if (!volume) return '-';
    if (volume >= 1000000) return `${(volume / 1000000).toFixed(2)}M`;
    if (volume >= 1000) return `${(volume / 1000).toFixed(0)}K`;
    return volume.toString();
  };

  const formatMarketCap = (marketCap) => {
    if (!marketCap || marketCap === 0) return '-';
    if (marketCap >= 1000000000) return `$${(marketCap / 1000000000).toFixed(2)}B`;
    if (marketCap >= 1000000) return `$${(marketCap / 1000000).toFixed(2)}M`;
    if (marketCap >= 1000) return `$${(marketCap / 1000).toFixed(2)}K`;
    return `$${marketCap.toFixed(0)}`;
  };

  const formatPrice = (price) => {
    if (!price) return '-';
    return `$${price.toFixed(2)}`;
  };

  const formatPercent = (percent) => {
    if (!percent && percent !== 0) return '-';
    const sign = percent > 0 ? '+' : '';
    return `${sign}${percent.toFixed(2)}%`;
  };

  const formatATR = (atr) => {
    if (!atr) return '-';
    return atr.toFixed(2);
  };

  const formatRSI = (rsi) => {
    if (!rsi) return '-';
    return rsi.toFixed(0);
  };

  const formatSpread = (bid, ask, price) => {
    if (!bid || !ask || !price || price === 0) return '-';
    const spread = ((ask - bid) / price) * 100;
    return `${spread.toFixed(2)}%`;
  };

  const calculateChangeFromOpen = (currentPrice, openPrice) => {
    if (!currentPrice || !openPrice || openPrice === 0) return '-';
    const change = ((currentPrice - openPrice) / openPrice) * 100;
    return formatPercent(change);
  };

  const handleStockClick = (stock) => {
    setSelectedStock(stock);
    if (stock.has_news) {
      fetchNews(stock.symbol);
    } else {
      setSelectedNews(null);
    }
  };

  const handleNewsClick = async (stock) => {
    try {
      // Add news to panel order at the TOP (most recent = top)
      setPanelOrder(prev => {
        if (!prev.includes('news')) {
          return ['news', ...prev]; // Add to beginning
        }
        // If already present, move it to top
        return ['news', ...prev.filter(p => p !== 'news')];
      });

      // Get scan date for filtering
      const scanDate = currentData?.timestamp ? currentData.timestamp.split('T')[0] : null;

      console.log(`📰 Loading news for ${stock.symbol}`);

      // First, check if stock has cached articles from the scan
      if (stock.news_articles && stock.news_articles.length > 0) {
        console.log(`📰 Using ${stock.news_articles.length} cached articles from scan`);
        setSelectedNews({
          symbol: stock.symbol,
          articles: stock.news_articles,
          loading: false,
          scanDate: scanDate
        });
        return; // Use cached articles, don't fetch
      }

      // No cached articles - show loading and fetch
      setSelectedNews({
        symbol: stock.symbol,
        loading: true
      });

      // Fetch new articles from Finnhub API
      const finnhubResponse = await axios.get(`${API_URL}/api/news/${stock.symbol}`);
      let newArticles = finnhubResponse.data.news || [];
      console.log(`📰 Fetched ${newArticles.length} articles from API for ${stock.symbol}`);

      // Display the articles
      setSelectedNews({
        symbol: stock.symbol,
        articles: newArticles,
        loading: false,
        scanDate: scanDate
      });

    } catch (error) {
      console.error('Error fetching news:', error);
      setSelectedNews({
        symbol: stock.symbol,
        articles: [],
        loading: false,
        error: 'Failed to fetch news'
      });
    }
  };

  const handlePriceClick = async (e, stock) => {
    e.stopPropagation();

    // Add chart to panel order at the TOP (most recent = top)
    setPanelOrder(prev => {
      if (!prev.includes('chart')) {
        return ['chart', ...prev]; // Add to beginning
      }
      // If already present, move it to top
      return ['chart', ...prev.filter(p => p !== 'chart')];
    });

    // Show loading state
    setSelectedChart({
      symbol: stock.symbol,
      loading: true,
      currentPrice: stock.price
    });

    try {
      // Determine scan date for chart request
      const now = new Date();
      const scanTime = currentData?.timestamp ? new Date(currentData.timestamp) : now;
      const scanDateStr = scanTime.toISOString().split('T')[0];
      const todayStr = now.toISOString().split('T')[0];

      // Pass date parameter if viewing historical scan, omit for current day
      const dateParam = scanDateStr !== todayStr ? `?date=${scanDateStr}` : '';

      console.log(`📊 Fetching chart for ${stock.symbol} - scan date: ${scanDateStr}, is today: ${scanDateStr === todayStr}`);

      // Fetch real chart data from Schwab API
      const response = await axios.get(`${API_URL}/api/chart/${stock.symbol}${dateParam}`);
      const chartData = response.data;

      if (chartData.error) {
        console.error('Chart data error:', chartData.error);
        // Fallback to mock data
        const generateMockData = () => {
          const points = [];
          const basePrice = stock.prev_close || stock.price * 0.95;
          const currentPrice = stock.price;

          // Generate data points every 5 minutes
          for (let i = 0; i < 78; i++) { // 78 5-minute intervals in a trading day
            const hour = 9.5 + (i * 0.0833);
            const timeStr = `${Math.floor(hour)}:${String(Math.floor((hour % 1) * 60)).padStart(2, '0')}`;

            const progress = i / 78;
            const trendPrice = basePrice + (currentPrice - basePrice) * progress;
            const randomWalk = (Math.random() - 0.5) * 0.02 * basePrice;
            const price = Math.max(0.01, trendPrice + randomWalk);

            points.push({
              time: timeStr,
              price: parseFloat(price.toFixed(2)),
              volume: Math.floor(stock.volume * (0.05 + Math.random() * 0.1))
            });
          }
          return points;
        };

        setSelectedChart({
          symbol: stock.symbol,
          data: generateMockData(),
          currentPrice: stock.price,
          prevClose: stock.prev_close || stock.price * 0.95,
          high: Math.max(stock.price * 1.05, stock.prev_close * 1.02),
          low: Math.min(stock.price * 0.95, stock.prev_close * 0.98),
          volume: stock.volume,
          loading: false
        });
      } else {
        // Process real Schwab data
        // Extract the open price from the first regular market hours candle (9:30 AM)
        let marketOpenPrice = null;
        if (chartData.candles && chartData.candles.length > 0) {
          // Find the first candle at or after 9:30 AM
          for (const candle of chartData.candles) {
            const candleTime = new Date(candle.datetime);
            const hours = candleTime.getHours();
            const minutes = candleTime.getMinutes();

            // Check if this is at or after 9:30 AM
            if ((hours === 9 && minutes >= 30) || hours > 9) {
              marketOpenPrice = candle.open;
              break;
            }
          }
        }

        // Update the stock's open_price if we found it
        if (marketOpenPrice && !stock.open_price) {
          // Update the current data with the real open price
          setCurrentData(prevData => {
            if (!prevData?.top_movers) return prevData;

            const updatedData = { ...prevData };
            Object.keys(updatedData.top_movers).forEach(tab => {
              updatedData.top_movers[tab] = updatedData.top_movers[tab].map(s =>
                s.symbol === stock.symbol
                  ? { ...s, open_price: marketOpenPrice }
                  : s
              );
            });
            return updatedData;
          });
        }

        setSelectedChart({
          symbol: chartData.symbol || stock.symbol,
          candles: chartData.candles || [],
          currentPrice: chartData.currentPrice || stock.price,
          previousClose: chartData.previousClose || stock.prev_close,
          dayHigh: chartData.dayHigh,
          dayLow: chartData.dayLow,
          volume: chartData.volume,
          marketOpenPrice: marketOpenPrice,
          loading: false
        });
      }
    } catch (error) {
      console.error('Failed to fetch chart data:', error);
      // Fallback to mock data on error
      const generateMockData = () => {
        const points = [];
        const basePrice = stock.prev_close || stock.price * 0.95;
        const currentPrice = stock.price;

        for (let i = 0; i < 78; i++) {
          const hour = 9.5 + (i * 0.0833);
          const timeStr = `${Math.floor(hour)}:${String(Math.floor((hour % 1) * 60)).padStart(2, '0')}`;
          const progress = i / 78;
          const trendPrice = basePrice + (currentPrice - basePrice) * progress;
          const randomWalk = (Math.random() - 0.5) * 0.02 * basePrice;
          const price = Math.max(0.01, trendPrice + randomWalk);

          points.push({
            time: timeStr,
            price: parseFloat(price.toFixed(2)),
            volume: Math.floor(stock.volume * (0.05 + Math.random() * 0.1))
          });
        }
        return points;
      };

      setSelectedChart({
        symbol: stock.symbol,
        data: generateMockData(),
        currentPrice: stock.price,
        prevClose: stock.prev_close || stock.price * 0.95,
        high: Math.max(stock.price * 1.05, stock.prev_close * 1.02),
        low: Math.min(stock.price * 0.95, stock.prev_close * 0.98),
        volume: stock.volume,
        loading: false,
        error: 'Using simulated data'
      });
    }
  };

  // Market overview stats
  const marketStats = useMemo(() => {
    if (!currentData) return null;

    return {
      totalScanned: currentData.total_stocks_scanned || 0,
      smallCapGappers: currentData.top_movers?.small_cap_gappers?.length || 0,
      largeCapGappers: currentData.top_movers?.large_cap_gappers?.length || 0,
      gapDowns: currentData.top_movers?.gap_downs?.length || 0,
      pennyStocks: currentData.top_movers?.penny_stock_movers?.length || 0
    };
  }, [currentData]);

  return (
    <div className="premarket-dashboard">
      {/* Header */}
      <div className="premarket-header">
        <div className="premarket-title">Pre-Market Scanner Dashboard</div>
        <div className="header-stats">
          <div className="stat">
            <span className="stat-label">Total Scanned</span>
            <span className="stat-value">{marketStats?.totalScanned || 0}</span>
          </div>
          <div className="stat">
            <span className="stat-label">Last Update</span>
            <span className="stat-value">
              {currentData?.scan_date ? format(new Date(currentData.scan_date), 'HH:mm:ss') : '-'}
            </span>
          </div>
        </div>
        <div className="header-controls">
          <button
            className={`auto-refresh-btn ${autoRefresh ? 'active' : ''}`}
            onClick={() => setAutoRefresh(!autoRefresh)}
          >
            {autoRefresh ? '⏸ Pause' : '▶️ Auto Refresh'}
          </button>
          <button
            className="refresh-btn"
            onClick={() => selectedScan && loadScan(selectedScan)}
          >
            🔄 Refresh
          </button>
        </div>
      </div>

      <div className="premarket-content">
        {/* Left Sidebar - Categories */}
        <div className="premarket-sidebar-left">
          {/* Scan Selector */}
          <div className="panel">
            <div className="panel-header">
              <h3>📅 Scan History</h3>
            </div>
            <div className="panel-content">
              <button
                className={`run-scan-btn ${scanRunning ? 'running' : ''}`}
                onClick={runPremarketScan}
                disabled={scanRunning}
              >
                {scanRunning ? '⏳ Running...' : '▶️ Run Premarket Scan'}
              </button>
              {scanRunning && (
                <div className="scan-progress">
                  <div className="progress-bar">
                    <div
                      className="progress-bar-fill"
                      style={{ width: `${scanPercent}%`, transition: 'width 0.3s ease' }}
                    ></div>
                  </div>
                  <div className="progress-text">
                    {scanPercent > 0 && scanPercent < 100 ? `${scanPercent.toFixed(1)}% - ` : ''}{scanProgress}
                  </div>
                </div>
              )}
              <button
                className={`run-scan-btn ${analyzingTopMovers ? 'running' : ''}`}
                onClick={analyzeTopMovers}
                disabled={analyzingTopMovers || !selectedScan}
                style={{ marginTop: '8px', background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)' }}
              >
                {analyzingTopMovers ? '⏳ Analyzing...' : '🎯 Analyze Top Movers'}
              </button>
              <select
                className="scan-selector"
                value={selectedScan || ''}
                onChange={(e) => e.target.value && loadScan(e.target.value)}
              >
                <option value="">Select a scan...</option>
                {scans.map((scan) => (
                  <option key={scan.id} value={scan.id}>
                    {scan.date && scan.time ? `${scan.date} - ${scan.time}` :
                     scan.timestamp ? new Date(scan.timestamp).toLocaleString() :
                     scan.filename || scan.id}
                  </option>
                ))}
              </select>
              {selectedScan && currentData && (
                <div className="scan-info">
                  <div className="scan-info-item">
                    <span>Time:</span>
                    <strong>{currentData.timestamp ? new Date(currentData.timestamp).toLocaleString() : 'N/A'}</strong>
                  </div>
                  <div className="scan-info-item">
                    <span>Total:</span>
                    <strong>{currentData.total_scanned || 0} stocks</strong>
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="panel">
            <div className="panel-header">
              <h3>Categories</h3>
            </div>
            <div className="panel-content">
              <div className="category-list">
                {Object.entries(watchlists).map(([key, watchlist]) => (
                  <div
                    key={key}
                    className={`category-item ${activeTab === key ? 'active' : ''}`}
                    onClick={() => setActiveTab(key)}
                  >
                    <span className="category-icon">{watchlist.icon}</span>
                    <span className="category-name">{watchlist.name}</span>
                    <span className="category-count">
                      {currentData?.top_movers?.[key]?.length || 0}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Market Overview */}
          <div className="panel">
            <div className="panel-header">
              <h3>Market Overview</h3>
            </div>
            <div className="panel-content">
              <div className="overview-stats">
                <div className="overview-item">
                  <span className="overview-label">Stocks Scanned</span>
                  <span className="overview-value">{marketStats?.totalScanned || 0}</span>
                </div>
                <div className="overview-item">
                  <span className="overview-label">Small Cap Gappers</span>
                  <span className="overview-value positive">{marketStats?.smallCapGappers || 0}</span>
                </div>
                <div className="overview-item">
                  <span className="overview-label">Large Cap Gappers</span>
                  <span className="overview-value positive">{marketStats?.largeCapGappers || 0}</span>
                </div>
                <div className="overview-item">
                  <span className="overview-label">Gap Downs</span>
                  <span className="overview-value negative">{marketStats?.gapDowns || 0}</span>
                </div>
                <div className="overview-item">
                  <span className="overview-label">Penny Movers</span>
                  <span className="overview-value">{marketStats?.pennyStocks || 0}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Filters */}
          <div className="panel">
            <div className="panel-header">
              <h3>Filters</h3>
              <button
                className="clear-filters-btn"
                onClick={() => setFilters({
                  minPrice: 0,
                  maxPrice: 10000,
                  minGap: -100,
                  maxGap: 100,
                  minVolume: '',
                  minRelVolume: '',
                  minMarketCap: '',
                  maxMarketCap: '',
                  minFloat: '',
                  maxFloat: '',
                  minRSI: '',
                  maxRSI: '',
                  sortBy: 'gap_pct',
                  sortDesc: true
                })}
              >
                Clear
              </button>
            </div>
            <div className="panel-content">
              <div className="filter-section">
                <label>Price Range ($)</label>
                <div className="filter-inputs">
                  <input
                    type="number"
                    value={filters.minPrice}
                    onChange={(e) => setFilters({...filters, minPrice: e.target.value ? Number(e.target.value) : ''})}
                    placeholder="Min"
                    className="filter-input"
                  />
                  <span>-</span>
                  <input
                    type="number"
                    value={filters.maxPrice}
                    onChange={(e) => setFilters({...filters, maxPrice: e.target.value ? Number(e.target.value) : ''})}
                    placeholder="Max"
                    className="filter-input"
                  />
                </div>
              </div>

              <div className="filter-section">
                <label>Gap % Range</label>
                <div className="filter-inputs">
                  <input
                    type="number"
                    value={filters.minGap}
                    onChange={(e) => setFilters({...filters, minGap: e.target.value ? Number(e.target.value) : ''})}
                    placeholder="Min"
                    className="filter-input"
                  />
                  <span>-</span>
                  <input
                    type="number"
                    value={filters.maxGap}
                    onChange={(e) => setFilters({...filters, maxGap: e.target.value ? Number(e.target.value) : ''})}
                    placeholder="Max"
                    className="filter-input"
                  />
                </div>
              </div>

              <div className="filter-section">
                <label>Min Volume</label>
                <input
                  type="number"
                  value={filters.minVolume || ''}
                  onChange={(e) => setFilters({...filters, minVolume: e.target.value ? Number(e.target.value) : ''})}
                  placeholder="e.g., 1000000"
                  className="filter-input full-width"
                />
              </div>

              <div className="filter-section">
                <label>Min Relative Volume</label>
                <input
                  type="number"
                  step="0.1"
                  value={filters.minRelVolume || ''}
                  onChange={(e) => setFilters({...filters, minRelVolume: e.target.value ? Number(e.target.value) : ''})}
                  placeholder="e.g., 2.0"
                  className="filter-input full-width"
                />
              </div>

              <div className="filter-section">
                <label>Market Cap ($)</label>
                <div className="filter-inputs">
                  <input
                    type="number"
                    value={filters.minMarketCap || ''}
                    onChange={(e) => setFilters({...filters, minMarketCap: e.target.value ? Number(e.target.value) : ''})}
                    placeholder="Min"
                    className="filter-input"
                  />
                  <span>-</span>
                  <input
                    type="number"
                    value={filters.maxMarketCap || ''}
                    onChange={(e) => setFilters({...filters, maxMarketCap: e.target.value ? Number(e.target.value) : ''})}
                    placeholder="Max"
                    className="filter-input"
                  />
                </div>
              </div>

              <div className="filter-section">
                <label>Float (Shares)</label>
                <div className="filter-inputs">
                  <input
                    type="number"
                    value={filters.minFloat || ''}
                    onChange={(e) => setFilters({...filters, minFloat: e.target.value ? Number(e.target.value) : ''})}
                    placeholder="Min"
                    className="filter-input"
                  />
                  <span>-</span>
                  <input
                    type="number"
                    value={filters.maxFloat || ''}
                    onChange={(e) => setFilters({...filters, maxFloat: e.target.value ? Number(e.target.value) : ''})}
                    placeholder="Max"
                    className="filter-input"
                  />
                </div>
              </div>

              <div className="filter-section">
                <label>RSI Range</label>
                <div className="filter-inputs">
                  <input
                    type="number"
                    value={filters.minRSI || ''}
                    onChange={(e) => setFilters({...filters, minRSI: e.target.value ? Number(e.target.value) : ''})}
                    placeholder="Min"
                    className="filter-input"
                  />
                  <span>-</span>
                  <input
                    type="number"
                    value={filters.maxRSI || ''}
                    onChange={(e) => setFilters({...filters, maxRSI: e.target.value ? Number(e.target.value) : ''})}
                    placeholder="Max"
                    className="filter-input"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Main Content - Stock Table */}
        <div className="premarket-main">
          {/* Market Overview Dashboard */}
          <MarketOverviewDark data={currentData} />

          <div className="panel stock-panel">
            <div className="panel-header">
              <h3>{watchlists[activeTab]?.icon} {watchlists[activeTab]?.name}</h3>
              <div className="panel-controls">
                <button className="export-btn">📥 Export CSV</button>
              </div>
            </div>
            <div className="stock-table-container">
              {loading ? (
                <div className="loading">Loading...</div>
              ) : (
                <table className="stock-table" ref={tableRef}>
                  <thead>
                    <tr>
                      <th style={{ width: columnWidths.symbol || 'auto' }}>
                        SYMBOL
                        <div
                          className="resize-handle"
                          onMouseDown={(e) => handleMouseDown(e, 'symbol')}
                        />
                      </th>
                      <th style={{ width: columnWidths.open_price || 'auto' }}>
                        OPEN
                        <div
                          className="resize-handle"
                          onMouseDown={(e) => handleMouseDown(e, 'open_price')}
                        />
                      </th>
                      <th style={{ width: columnWidths.close_price || 'auto' }}>
                        CLOSE
                        <div
                          className="resize-handle"
                          onMouseDown={(e) => handleMouseDown(e, 'close_price')}
                        />
                      </th>
                      <th style={{ width: columnWidths.price || 'auto' }}>
                        LAST
                        <div
                          className="resize-handle"
                          onMouseDown={(e) => handleMouseDown(e, 'price')}
                        />
                      </th>
                      <th
                        className="sortable"
                        style={{ width: columnWidths.gap_pct || 'auto' }}
                        onClick={() => setFilters({
                          ...filters,
                          sortBy: 'gap_pct',
                          sortDesc: filters.sortBy === 'gap_pct' ? !filters.sortDesc : true
                        })}
                      >
                        GAP % {filters.sortBy === 'gap_pct' && (filters.sortDesc ? '↓' : '↑')}
                        <div
                          className="resize-handle"
                          onMouseDown={(e) => handleMouseDown(e, 'gap_pct')}
                          onClick={(e) => e.stopPropagation()}
                        />
                      </th>
                      <th
                        className="sortable"
                        style={{ width: columnWidths.change_from_open || 'auto' }}
                        onClick={() => setFilters({
                          ...filters,
                          sortBy: 'change_from_open',
                          sortDesc: filters.sortBy === 'change_from_open' ? !filters.sortDesc : true
                        })}
                      >
                        OPEN % {filters.sortBy === 'change_from_open' && (filters.sortDesc ? '↓' : '↑')}
                        <div
                          className="resize-handle"
                          onMouseDown={(e) => handleMouseDown(e, 'change_from_open')}
                          onClick={(e) => e.stopPropagation()}
                        />
                      </th>
                      <th
                        className="sortable"
                        style={{ width: columnWidths.volume || 'auto' }}
                        onClick={() => setFilters({
                          ...filters,
                          sortBy: 'volume',
                          sortDesc: filters.sortBy === 'volume' ? !filters.sortDesc : true
                        })}
                      >
                        VOLUME {filters.sortBy === 'volume' && (filters.sortDesc ? '↓' : '↑')}
                        <div
                          className="resize-handle"
                          onMouseDown={(e) => handleMouseDown(e, 'volume')}
                          onClick={(e) => e.stopPropagation()}
                        />
                      </th>
                      <th style={{ width: columnWidths.rel_vol || 'auto' }}>
                        REL VOL
                        <div
                          className="resize-handle"
                          onMouseDown={(e) => handleMouseDown(e, 'rel_vol')}
                        />
                      </th>
                      <th
                        className="sortable"
                        style={{ width: columnWidths.atr || 'auto' }}
                        onClick={() => setFilters({
                          ...filters,
                          sortBy: 'atr',
                          sortDesc: filters.sortBy === 'atr' ? !filters.sortDesc : true
                        })}
                      >
                        ATR {filters.sortBy === 'atr' && (filters.sortDesc ? '↓' : '↑')}
                        <div
                          className="resize-handle"
                          onMouseDown={(e) => handleMouseDown(e, 'atr')}
                          onClick={(e) => e.stopPropagation()}
                        />
                      </th>
                      <th
                        className="sortable"
                        style={{ width: columnWidths.rsi || 'auto' }}
                        onClick={() => setFilters({
                          ...filters,
                          sortBy: 'rsi',
                          sortDesc: filters.sortBy === 'rsi' ? !filters.sortDesc : true
                        })}
                      >
                        RSI {filters.sortBy === 'rsi' && (filters.sortDesc ? '↓' : '↑')}
                        <div
                          className="resize-handle"
                          onMouseDown={(e) => handleMouseDown(e, 'rsi')}
                          onClick={(e) => e.stopPropagation()}
                        />
                      </th>
                      <th style={{ width: columnWidths.spread || 'auto' }}>
                        SPREAD %
                        <div
                          className="resize-handle"
                          onMouseDown={(e) => handleMouseDown(e, 'spread')}
                        />
                      </th>
                      <th
                        className="sortable"
                        style={{ width: columnWidths.market_cap || 'auto' }}
                        onClick={() => setFilters({
                          ...filters,
                          sortBy: 'market_cap',
                          sortDesc: filters.sortBy === 'market_cap' ? !filters.sortDesc : true
                        })}
                      >
                        MARKET CAP {filters.sortBy === 'market_cap' && (filters.sortDesc ? '↓' : '↑')}
                        <div
                          className="resize-handle"
                          onMouseDown={(e) => handleMouseDown(e, 'market_cap')}
                          onClick={(e) => e.stopPropagation()}
                        />
                      </th>
                      <th
                        className="sortable"
                        style={{ width: columnWidths.float_shares || 'auto' }}
                        onClick={() => setFilters({
                          ...filters,
                          sortBy: 'float_shares',
                          sortDesc: filters.sortBy === 'float_shares' ? !filters.sortDesc : true
                        })}
                      >
                        FLOAT {filters.sortBy === 'float_shares' && (filters.sortDesc ? '↓' : '↑')}
                        <div
                          className="resize-handle"
                          onMouseDown={(e) => handleMouseDown(e, 'float_shares')}
                          onClick={(e) => e.stopPropagation()}
                        />
                      </th>
                      <th style={{ width: columnWidths.news || 'auto' }}>
                        NEWS
                        <div
                          className="resize-handle"
                          onMouseDown={(e) => handleMouseDown(e, 'news')}
                        />
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {currentWatchlist.map((stock) => (
                      <tr key={stock.symbol} onClick={() => handleStockClick(stock)}>
                        <td className="symbol">{stock.symbol}</td>
                        <td>{stock.open_price ? formatPrice(stock.open_price) : '-'}</td>
                        <td>
                          {(() => {
                            // Show CLOSE only if: (1) not today's scan OR (2) market is closed
                            const showClose = !isTodayScan(currentData) || isMarketClosed();
                            if (showClose && stock.close_price) {
                              return formatPrice(stock.close_price);
                            }
                            return '-';
                          })()}
                        </td>
                        <td className="clickable-price" onClick={(e) => handlePriceClick(e, stock)}>{formatPrice(stock.price)}</td>
                        <td className={stock.gap_pct >= 0 ? 'gain-positive' : 'gain-negative'}>
                          {formatPercent(stock.gap_pct)}
                        </td>
                        <td className={stock.change_from_open === null ? '' : stock.change_from_open >= 0 ? 'gain-positive' : 'gain-negative'}>
                          {stock.change_from_open === null ? 'N/A' : formatPercent(stock.change_from_open)}
                        </td>
                        <td>{formatVolume(stock.volume)}</td>
                        <td>
                          {stock.avg_volume && stock.avg_volume > 0
                            ? `${(stock.volume / stock.avg_volume).toFixed(1)}x`
                            : '-'
                          }
                        </td>
                        <td>{formatATR(stock.atr)}</td>
                        <td className={stock.rsi > 70 ? 'rsi-overbought' : stock.rsi < 30 ? 'rsi-oversold' : ''}>
                          {formatRSI(stock.rsi)}
                        </td>
                        <td>{formatSpread(stock.bid, stock.ask, stock.price)}</td>
                        <td>{formatMarketCap(stock.market_cap)}</td>
                        <td>{stock.float_shares ? formatVolume(stock.float_shares) : '-'}</td>
                        <td className="news-cell" onClick={(e) => {
                          e.stopPropagation();
                          if (stock.has_news) handleNewsClick(stock);
                        }}>
                          {stock.has_news ? '📰' : '-'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>

        {/* Right Sidebar - News and Charts (only show if panels exist) */}
        {(selectedNews || selectedChart) && (
          <div className="premarket-sidebar-right">
            {/* Render panels in order of clicks */}
            {panelOrder.map(panelType => {
              if (panelType === 'news' && selectedNews) {
                return (
                  <div className="panel" key="news">
                    <div className="panel-header">
                      <h3>{selectedNews.symbol} News</h3>
                      <button
                        className="close-news"
                        onClick={() => {
                          setSelectedNews(null);
                          setPanelOrder(prev => prev.filter(p => p !== 'news'));
                        }}
                      >
                        ×
                      </button>
                    </div>
                    <div className="panel-content news-section">
                      {selectedNews.loading ? (
                        <div className="news-loading">
                          <div className="spinner"></div>
                          <p>Loading news...</p>
                        </div>
                      ) : selectedNews.error ? (
                        <div className="news-error">{selectedNews.error}</div>
                      ) : selectedNews.articles?.length > 0 ? (
                        <div className="news-list-sidebar">
                          {selectedNews.articles.map((article, index) => (
                            <div
                              key={index}
                              className="news-item-sidebar"
                              onClick={() => window.open(article.url, '_blank')}
                            >
                              <div className="news-header">
                                <span className="news-source">{article.source}</span>
                                <span className="news-time">
                                  {new Date(article.datetime).toLocaleDateString()}
                                </span>
                              </div>
                              <h4 className="news-headline">{article.headline}</h4>
                              <p className="news-summary">{article.summary}</p>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="no-news">No recent news available for {selectedNews.symbol}</div>
                      )}
                    </div>
                  </div>
                );
              }

              if (panelType === 'chart' && selectedChart) {
                return (
                  <div className="panel" key="chart">
                    <div className="panel-header">
                      <h3>{selectedChart.symbol} Intraday Chart</h3>
                      <div style={{ display: 'flex', gap: '8px' }}>
                        <button
                          className="expand-chart-btn"
                          onClick={() => setChartModal(selectedChart)}
                          title="Expand chart"
                        >
                          ⛶
                        </button>
                        <button
                          className="close-panel"
                          onClick={() => {
                            setSelectedChart(null);
                            setPanelOrder(prev => prev.filter(p => p !== 'chart'));
                          }}
                        >
                          ×
                        </button>
                      </div>
                    </div>
                    <div className="panel-content" onClick={() => setChartModal(selectedChart)} style={{ cursor: 'pointer' }}>
                      <D3CandlestickChart chartData={selectedChart} />
                    </div>
                  </div>
                );
              }

              return null;
            })}

          {/* Stock Details */}
          {selectedStock && (
            <div className="panel">
              <div className="panel-header">
                <h3>Stock Details</h3>
                <button className="close-panel" onClick={() => setSelectedStock(null)}>×</button>
              </div>
              <div className="panel-content">
                <div className="stock-details">
                  <div className="detail-row">
                    <span>Symbol</span>
                    <span className="detail-value">{selectedStock.symbol}</span>
                  </div>
                  <div className="detail-row">
                    <span>Price</span>
                    <span className="detail-value">{formatPrice(selectedStock.price)}</span>
                  </div>
                  <div className="detail-row">
                    <span>Gap %</span>
                    <span className={`detail-value ${selectedStock.gap_pct >= 0 ? 'gain-positive' : 'gain-negative'}`}>
                      {formatPercent(selectedStock.gap_pct)}
                    </span>
                  </div>
                  <div className="detail-row">
                    <span>% From Open</span>
                    <span className={`detail-value ${selectedStock.change_from_open >= 0 ? 'gain-positive' : 'gain-negative'}`}>
                      {calculateChangeFromOpen(selectedStock.price, selectedStock.open_price)}
                    </span>
                  </div>
                  <div className="detail-row">
                    <span>Volume</span>
                    <span className="detail-value">{formatVolume(selectedStock.volume)}</span>
                  </div>
                  <div className="detail-row">
                    <span>Relative Volume</span>
                    <span className="detail-value">
                      {selectedStock.avg_volume && selectedStock.avg_volume > 0
                        ? `${(selectedStock.volume / selectedStock.avg_volume).toFixed(1)}x`
                        : '-'
                      }
                    </span>
                  </div>
                  <div className="detail-row">
                    <span>ATR</span>
                    <span className="detail-value">{formatATR(selectedStock.atr)}</span>
                  </div>
                  <div className="detail-row">
                    <span>RSI</span>
                    <span className={`detail-value ${selectedStock.rsi > 70 ? 'rsi-overbought' : selectedStock.rsi < 30 ? 'rsi-oversold' : ''}`}>
                      {formatRSI(selectedStock.rsi)}
                    </span>
                  </div>
                  <div className="detail-row">
                    <span>Spread %</span>
                    <span className="detail-value">{formatSpread(selectedStock.bid, selectedStock.ask, selectedStock.price)}</span>
                  </div>
                  <div className="detail-row">
                    <span>Float</span>
                    <span className="detail-value">
                      {selectedStock.float_shares ? formatVolume(selectedStock.float_shares) : '-'}
                    </span>
                  </div>
                  <div className="detail-row">
                    <span>Market Cap</span>
                    <span className="detail-value">
                      {selectedStock.market_cap ? formatVolume(selectedStock.market_cap) : '-'}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          )}
          </div>
        )}
      </div>

      {/* Status Bar */}
      <div className="status-bar">
        <div className="status-left">
          <div className="connection-status">
            <span className={`status-dot ${loading ? 'loading' : 'connected'}`}></span>
            <span>{loading ? 'Loading...' : 'Connected'}</span>
          </div>
        </div>
        <div className="status-center">
          <span>Last Scan: {currentData?.scan_date ? format(new Date(currentData.scan_date), 'MMM dd, HH:mm:ss') : '-'}</span>
        </div>
        <div className="status-right">
          <span>{currentWatchlist.length} stocks</span>
        </div>
      </div>

      {/* Chart Modal */}
      {chartModal && (
        <div className="chart-modal-overlay" onClick={() => setChartModal(null)}>
          <div className="chart-modal" onClick={(e) => e.stopPropagation()}>
            <div className="chart-modal-header">
              <h2>{chartModal.symbol} Intraday Chart</h2>
              <button className="close-modal" onClick={() => setChartModal(null)}>
                ×
              </button>
            </div>
            <div className="chart-modal-content">
              <D3CandlestickChart chartData={chartModal} />
            </div>
          </div>
        </div>
      )}

      {/* Top Movers Modal */}
      {topMoversModal && (
        <div className="chart-modal-overlay" onClick={() => setTopMoversModal(null)}>
          <div className="chart-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '1200px' }}>
            <div className="chart-modal-header">
              <h2>🎯 Top Movers Analysis - Best Opportunities</h2>
              <button className="close-modal" onClick={() => setTopMoversModal(null)}>
                ×
              </button>
            </div>
            <div className="chart-modal-content" style={{ padding: '20px' }}>
              <div style={{ marginBottom: '16px', color: '#9ca3af', fontSize: '14px' }}>
                <strong>{topMoversModal.totalFiltered}</strong> stocks match criteria (sorted by Gap %)
              </div>
              <div style={{ marginBottom: '16px', padding: '12px', background: '#1a1a1f', borderRadius: '8px', fontSize: '12px' }}>
                <div style={{ color: '#10b981', marginBottom: '4px' }}><strong>Tier 1:</strong> $1-$10, Has News, Gap ≥ 5%, Volume &gt; 100K</div>
                <div style={{ color: '#fbbf24' }}><strong>Tier 2:</strong> $1-$10, Gap ≥ 3%, Volume &gt; 100K (no news required)</div>
              </div>

              <div style={{ overflowX: 'auto' }}>
                <table className="stock-table" style={{ width: '100%' }}>
                  <thead>
                    <tr>
                      <th>Rank</th>
                      <th>Tier</th>
                      <th>Symbol</th>
                      <th>Price</th>
                      <th>Gap %</th>
                      <th>Volume</th>
                      <th>News</th>
                      <th>Latest Headline</th>
                      <th>52w Range</th>
                    </tr>
                  </thead>
                  <tbody>
                    {topMoversModal.topMovers.map((stock, index) => (
                      <tr key={stock.symbol}>
                        <td style={{ fontWeight: '700', color: '#8b5cf6' }}>#{index + 1}</td>
                        <td style={{
                          fontWeight: '600',
                          color: stock.tier === 1 ? '#10b981' : '#fbbf24',
                          fontSize: '11px'
                        }}>
                          {stock.tier === 1 ? 'T1' : 'T2'}
                        </td>
                        <td className="symbol-cell">{stock.symbol}</td>
                        <td>{formatPrice(stock.price)}</td>
                        <td className={stock.gap_pct >= 0 ? 'gain-positive' : 'gain-negative'}>
                          {formatPercent(stock.gap_pct)}
                        </td>
                        <td>{formatVolume(stock.volume)}</td>
                        <td>{stock.news_count || 0}</td>
                        <td style={{ maxWidth: '300px', whiteSpace: 'normal', fontSize: '12px' }}>
                          {stock.latest_headline || '-'}
                        </td>
                        <td style={{ fontSize: '11px' }}>
                          {stock.fifty_two_week_low && stock.fifty_two_week_high
                            ? `$${stock.fifty_two_week_low.toFixed(2)} - $${stock.fifty_two_week_high.toFixed(2)}`
                            : '-'
                          }
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PreMarketDashboard;